import React, { useEffect, useMemo } from 'react';
import { PromptVariable } from '../../types';
import { useFetchedPromptVariables } from '../hooks/useFetchedPromptVariables';
import { getDefaultVariableValue } from '../../util';
import clsx from 'clsx';

type VariableMetadata = {
  name: string;
  isSuggested: boolean;
};
const VARIABLE_METADATAS: VariableMetadata[] = [
  { name: 'company', isSuggested: true },
  { name: 'product or service', isSuggested: true },
  { name: 'feature', isSuggested: true },
  { name: 'audience', isSuggested: true },
  { name: 'topic', isSuggested: false },
  { name: 'social media', isSuggested: false },
  { name: 'industry', isSuggested: false },
  { name: 'keyword', isSuggested: false },
  { name: 'value', isSuggested: false },
];

type PromptVariableViewModel = PromptVariable & {
  isSuggested: boolean;
  sortOrder: number;
  isFilled: boolean;
};

const shouldSuggestVariable = (viewModel: PromptVariableViewModel) =>
  viewModel.isSuggested && !viewModel.isFilled;

const VariableListItem = (props: {
  variable: PromptVariable;
  isSelected: boolean;
  onVariableSelected: (promptVariable: PromptVariable) => void;
}) => {
  const { variable, isSelected, onVariableSelected } = props;

  return (
    <div
      className={clsx('variable-list__item-container', {
        'variable-list__item-container--selected': isSelected,
      })}
      data-test="variable-list__item-container"
      onClick={() => onVariableSelected(variable)}
    >
      <div className="variable-list__item-name" data-test="variable-list__item-name">
        {variable.variableValue.name}
      </div>
    </div>
  );
};

const isPromptVariableFilled = (promptVariable: PromptVariable) =>
  promptVariable.variableValue.type === 'string'
    ? promptVariable.variableValue.value.trim().length > 0 ||
      promptVariable.variableValue.description.trim().length > 0
    : promptVariable.variableValue.values.length > 0 &&
      promptVariable.variableValue.values.some(
        (x) => x.value.trim().length > 0 || x.description.trim().length > 0
      );

export const VariableList = (props: {
  selectedVariable: PromptVariable | null;
  onVariableSelected: (promptVariable: PromptVariable | null) => void;
}) => {
  const { selectedVariable, onVariableSelected } = props;
  const {
    query: { data: variables },
    queryStatusComponent: variablesQueryStatusComponent,
  } = useFetchedPromptVariables();

  const variableViewModels: PromptVariableViewModel[] = useMemo(
    () =>
      (variables ?? [])
        .filter((x) => !VARIABLE_METADATAS.some((m) => x.variableValue.name === m.name))
        .map(
          (x): PromptVariableViewModel => ({
            ...x,
            isSuggested: false,
            sortOrder: VARIABLE_METADATAS.length,
            isFilled: isPromptVariableFilled(x),
          })
        )
        .concat(
          VARIABLE_METADATAS.map((m, index) => {
            const promptVariable: PromptVariable = (variables ?? []).find(
              (x) => x.variableValue.name === m.name
            ) ?? {
              id: null,
              isPrivate: false,
              variableValue: getDefaultVariableValue(m.name),
            };
            return {
              ...promptVariable,
              isSuggested: m.isSuggested,
              sortOrder: index,
              isFilled: isPromptVariableFilled(promptVariable),
            };
          })
        ),
    [variables]
  );

  useEffect(() => {
    if (!selectedVariable) return;
    onVariableSelected(
      variableViewModels?.find(
        (x) => x.variableValue.name === selectedVariable.variableValue.name
      ) ?? null
    );
  }, [variableViewModels, selectedVariable, onVariableSelected]);

  const suggestedViewModels = variableViewModels.filter((x) => shouldSuggestVariable(x));
  return variables ? (
    <div className="variable-list__container">
      {suggestedViewModels.length > 0 && (
        <div
          className="variable-list__item-sub-container--suggested"
          data-test="variable-list__item-sub-container--suggested"
        >
          <div className="variable-list__suggested-title">Suggested to fill in</div>
          {suggestedViewModels
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((x) => (
              <VariableListItem
                key={`variable-${x.variableValue.name}`}
                variable={x}
                isSelected={selectedVariable?.variableValue.name === x.variableValue.name}
                onVariableSelected={onVariableSelected}
              />
            ))}
        </div>
      )}
      <div data-test="variable-list__item-sub-container--normal">
        {variableViewModels
          .filter((x) => !shouldSuggestVariable(x))
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map((x) => (
            <VariableListItem
              key={`variable-${x.variableValue.name}`}
              variable={x}
              isSelected={selectedVariable?.variableValue.name === x.variableValue.name}
              onVariableSelected={onVariableSelected}
            />
          ))}
      </div>
    </div>
  ) : (
    variablesQueryStatusComponent
  );
};
